import { IoEllipsisVertical } from 'react-icons/io5';
import { useRef, useState, useEffect } from "react";
import { FreelancerRole, IFreelancer, RestrictedFreelancerRole } from "../../data_models/freelancer";
import Drawer from './Drawer';
import { getFreelancerName } from '../../utils/freelancerUtils';
import { DEFAULT_USER_PROFILE } from '../../config/defaultAssetUrls';
import { FilledButton, FlatButton, OutlinedButton } from '../common/Button';
import { ChatNowButton, InviteForTasksButton, ShortlistFreelancerButton } from '../common/GhButtons';
import useRouting from '../../hooks/useRouting';
import { EUserType } from '../../data_models/enums';
import { IconBase } from 'react-icons';
import IconButton from '../common/IconButton';
import useCurrentUser from '../../hooks/useCurrentUser';
import ShortlistFreelancerLightbox from '../lightboxes/projects/ShortlistFreelancerLightbox';
import InviteToTasksLightbox from '../lightboxes/projects/InviteToTasksLightbox';
import RichText from '../common/RichText';
import { UserEnumTextMapper } from '../../data_models/enumToTextMapper';
import { countriesToTextMapper } from '../../data_models/data/countries';
import SkillExperience from '../profile/common/SkillExperience';

interface FreelancerProfileDrawerProps {
    freelancer: IFreelancer;
    open: boolean;
    setOpen: (open: boolean) => void;
}

const FreelancerProfileDrawer: React.FC<FreelancerProfileDrawerProps> = ({ freelancer, open, setOpen }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [showMenu, setShowMenu] = useState(false);

    const { goToUserProfile } = useRouting();
    const { currentUser, currentUserType } = useCurrentUser();
    const isCurrentUser = currentUser?._id === freelancer._id && currentUserType !== EUserType.EMPLOYER;

    const [canCloseDrawer, setCanCloseDrawer] = useState(true);
    const [canChat, setCanChat] = useState(false);

    const [hasExtraInteractions, setHasExtraInteractions] = useState(false);
    const [canInviteForTasks, setCanInviteForTasks] = useState(false);
    const [canShortlist, setCanShortlist] = useState(false);

    const [showShortlistFreelancerLightbox, setShowShortlistFreelancerLightbox] = useState(false);
    const [showInviteToTaskLightbox, setShowInviteToTaskLightbox] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                canCloseDrawer && setShowMenu(false);
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    useEffect(() => {


        if (currentUser && currentUserType === EUserType.FREELANCER) {
            if ((currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)) {
                setCanInviteForTasks(true);
                setCanShortlist(true);
                setCanChat(true);
            }
        } else if (currentUser && currentUserType === EUserType.EMPLOYER) {
            setCanChat(true);
            setCanShortlist(true);
            setCanInviteForTasks(true);
        }

        if (canInviteForTasks || canShortlist) {
            setHasExtraInteractions(true);
        }
    }, [currentUserType, currentUser, canInviteForTasks, canShortlist]);

    useEffect(() => {
        if (showShortlistFreelancerLightbox || showInviteToTaskLightbox) {
            console.log('setting canCloseDrawer to false');
            setCanCloseDrawer(false);
        } else {
            console.log('setting canCloseDrawer to true');
            setCanCloseDrawer(true);
        }

    }, [showShortlistFreelancerLightbox, showInviteToTaskLightbox]);

    return (
        <Drawer open={open} setOpen={setOpen} title="Profile" canClose={canCloseDrawer}>
            <div className="divide-y divide-gray-200">
                <div className="pb-6">
                    <div className="h-24 bg-primary sm:h-20 lg:h-28" />
                    <div className="-mt-12 flow-root px-4 sm:-mt-8 sm:flex sm:items-end sm:px-6 lg:-mt-16">
                        <div>
                            <div className="-m-1 flex">
                                <div className="inline-flex overflow-hidden rounded-full border-4 border-white">
                                    <img
                                        alt=""
                                        src={freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                                        className="size-24 shrink-0 sm:size-40 lg:size-48 object-cover"
                                    />

                                </div>
                            </div>
                        </div>
                        <div className="mt-6 sm:ml-6 sm:flex-1">
                            <div className="flex items-center">
                                <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">{getFreelancerName(freelancer)}</h3>
                            </div>
                            <p className="text-sm text-gray-500">{(freelancer.roles || [FreelancerRole.OTHER]).map(role => UserEnumTextMapper.freelancerRole(role)).join(", ")}</p>

                            <div className="mt-5 flex flex-wrap items-center  space-x-3 sm:space-y-0 sm:space-x-3">
                                {canChat && <div className="flex-1 w-full">
                                    <ChatNowButton
                                        variant="filled"
                                        user={freelancer}
                                        size="medium"
                                        fullWidth
                                    />
                                </div>}
                                <div className="flex-1">
                                    <OutlinedButton fullWidth onClick={() => {
                                        setOpen(false);
                                        goToUserProfile(EUserType.FREELANCER, freelancer._id!);
                                    }}>Profile</OutlinedButton>
                                </div>

                                {hasExtraInteractions && <div className="ml-3 inline-flex sm:ml-0" ref={menuRef}>
                                    <IconButton
                                        icon={<IoEllipsisVertical />}
                                        onClick={() => setShowMenu(!showMenu)}
                                        theme="primary"
                                    />


                                    <div className={`absolute flex flex-col space-y-2 z-10 mt-10 right-5 w-48 rounded-md bg-white border border-gray-100 p-3 shadow-lg transition-opacity duration-100
                                        ${showMenu
                                            ? 'opacity-100'
                                            : 'opacity-0'}`}>
                                        <OutlinedButton size="medium" fullWidth onClick={() => {
                                            setShowShortlistFreelancerLightbox(true);
                                        }}>Shortlist</OutlinedButton>
                                        <OutlinedButton size="medium" fullWidth onClick={() => {
                                            setShowInviteToTaskLightbox(true);
                                        }}>Invite for tasks</OutlinedButton>
                                    </div>

                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="px-4 py-5 sm:px-0 sm:py-0">
                    <dl className="space-y-8 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                        {/* Bio */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Bio</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                                <RichText text={freelancer.bio || '-'} />
                            </dd>
                        </div>

                        {/* Country */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Country</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">{freelancer.country ? countriesToTextMapper(freelancer.country) : '-'}</dd>
                        </div>

                        {/* Languages */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                        <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Languages</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">{(freelancer.languages?.length || 0) > 0 ? freelancer.languages!.join(', ') : '-'}</dd>
                        </div>

                        {/* Skills */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Skills</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6 w-full">{(freelancer.skills || []).length > 0
                                ? freelancer.skills!.map((skill) => (
                                    <div key={skill.skill} className='flex flex-col w-full'>
                                        <SkillExperience
                                            skill={skill.skill}
                                            experience={skill.experience}
                                            experienceLabel={UserEnumTextMapper.freelancerExperienceLevel(skill.experience)}
                                        />
                                    </div>
                                ))
                                : '-'}</dd>
                        </div>

                        {/* Tools */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Tools</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6 w-full">{(freelancer.tools || []).length > 0
                                ? freelancer.tools!.map((tool) => (
                                    <div key={tool.tool} className='flex flex-col w-full'>
                                        <SkillExperience
                                            theme='secondary'
                                            skill={tool.tool}
                                            experience={tool.experience}
                                            experienceLabel={UserEnumTextMapper.freelancerExperienceLevel(tool.experience)}
                                        />
                                    </div>
                                ))
                                : '-'}</dd>
                        </div>

                        {/* Industries */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                            <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">Industries</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6 w-full">{
                                (freelancer.industries?.length || 0) > 0
                                ? freelancer.industries!.join(', ')
                                : '-'}</dd>
                        </div>
                    </dl>
                </div>


            </div>

            {showShortlistFreelancerLightbox && <ShortlistFreelancerLightbox
                onClose={() => { setShowShortlistFreelancerLightbox(false) }}
                freelancer={freelancer} />}

            {showInviteToTaskLightbox && <InviteToTasksLightbox
                onClose={() => { setShowInviteToTaskLightbox(false) }}
                freelancer={freelancer} />}
        </Drawer>
    );
};

export default FreelancerProfileDrawer;